import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, XCircle, ArrowLeft, Home } from 'lucide-react';
import { supabase } from '../utils/supabase';
type PaymentStatus = 'loading' | 'paid' | 'pending' | 'failed' | 'not_found';
// Mapeo de las tablas que tienen pagos a sus nombres "human-friendly"
const TABLES_WITH_PAYMENTS = [{
  table: 'registrations_5k',
  label: 'Caminata 5K'
}, {
  table: 'expositor_reservations',
  label: 'Reserva de stand'
}, {
  table: 'toldos_reservations',
  label: 'Reserva de toldo'
}, {
  table: 'sponsor_inquiries',
  label: 'Patrocinio'
}, {
  table: 'sports_team_registrations',
  label: 'Inscripción deportiva'
}] as const;
export function GraciasPage() {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get('id') || searchParams.get('ref');
  // Wompi appends its own transaction id as a second `id` param on the redirect URL
  const allIds = searchParams.getAll('id');
  const wompiTxId = allIds.length > 1 ? allIds[allIds.length - 1] : null;
  const env = searchParams.get('env'); // 'test' | 'prod' | null
  const [status, setStatus] = useState<PaymentStatus>('loading');
  const [registration, setRegistration] = useState<any>(null);
  const [registrationType, setRegistrationType] = useState<string>('');
  const [matchedTable, setMatchedTable] = useState<string | null>(null);
  const [wompiFallbackChecked, setWompiFallbackChecked] = useState(false);
  useEffect(() => {
    if (!reference) {
      setStatus('not_found');
      return;
    }
    let cancelled = false;
    let pollCount = 0;
    const MAX_POLLS = 20; // 20 × 3s = 60s
    // Query Wompi's public transactions API as a safety net
    // when the webhook hasn't updated Supabase yet.
    async function checkWompiDirectly(table: string): Promise<boolean> {
      if (!wompiTxId) return false;
      const base = env === 'test' ? 'https://sandbox.wompi.co/v1' : 'https://production.wompi.co/v1';
      try {
        const resp = await fetch(`${base}/transactions/${wompiTxId}`);
        const json = await resp.json();
        const wompiStatus: string | undefined = json?.data?.status;
        if (!wompiStatus) return false;
        if (wompiStatus === 'APPROVED') {
          await supabase.from(table).update({
            status: 'paid',
            paid_at: new Date().toISOString(),
            wompi_transaction_id: wompiTxId
          }).eq('id', reference);
          if (!cancelled) setStatus('paid');
          return true;
        }
        if (wompiStatus === 'DECLINED' || wompiStatus === 'VOIDED' || wompiStatus === 'ERROR') {
          await supabase.from(table).update({
            status: 'declined',
            wompi_transaction_id: wompiTxId
          }).eq('id', reference);
          if (!cancelled) setStatus('failed');
          return true;
        }
        // PENDING — leave Supabase as is
        return false;
      } catch (err) {
        console.warn('Wompi direct check failed:', err);
        return false;
      }
    }
    async function checkStatus() {
      // Buscar el registro en cada tabla con pagos
      for (const {
        table,
        label
      } of TABLES_WITH_PAYMENTS) {
        const {
          data
        } = await supabase.from(table).select('*').eq('id', reference).maybeSingle();
        if (data) {
          if (cancelled) return;
          setRegistration(data);
          setRegistrationType(label);
          setMatchedTable(table);
          if (data.status === 'paid') {
            setStatus('paid');
            return;
          }
          if (data.status === 'expired' || data.status === 'refunded' || data.status === 'declined' || data.status === 'voided' || data.status === 'error') {
            setStatus('failed');
            return;
          }
          // pending_payment → seguir polling
          setStatus('pending');
          // Safety net: try Wompi's public API once
          if (!wompiFallbackChecked && wompiTxId) {
            setWompiFallbackChecked(true);
            const resolved = await checkWompiDirectly(table);
            if (resolved) return;
          }
          return;
        }
      }
      if (cancelled) return;
      setStatus('not_found');
    }
    checkStatus();
    // Si está pending, hacer polling cada 3 segundos hasta que cambie o pasen 60s
    const interval = setInterval(async () => {
      pollCount++;
      if (pollCount > MAX_POLLS) {
        clearInterval(interval);
        return;
      }
      if (status === 'pending') {
        await checkStatus();
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);
  return <div className="pt-20 min-h-screen bg-gradient-to-b from-white to-gray-50" data-id="element-2418">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-2419">
        <Link to="/" className="inline-flex items-center gap-2 text-brand-cyan hover:text-brand-navy transition-colors mb-8 text-sm font-medium" data-id="element-2420">
          <ArrowLeft className="w-4 h-4" data-id="element-2421" /> Volver al inicio
        </Link>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-3xl p-10 md:p-14 shadow-xl border border-gray-100 text-center" data-id="element-2422">
          {status === 'loading' && <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center animate-pulse" data-id="element-2423">
                <Clock className="w-10 h-10 text-gray-400" data-id="element-2424" />
              </div>
              <h1 className="text-3xl font-bold text-brand-navy mb-3" data-id="element-2425">
                Verificando tu pago…
              </h1>
              <p className="text-gray-500" data-id="element-2426">Un momento por favor.</p>
            </>}

          {status === 'pending' && <>
              <motion.div animate={{
            scale: [1, 1.05, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-2427">
                <Clock className="w-10 h-10 text-amber-500" data-id="element-2428" />
              </motion.div>
              <h1 className="text-3xl font-bold text-brand-navy mb-3" data-id="element-2429">
                Procesando tu pago…
              </h1>
              <p className="text-gray-600 mb-2" data-id="element-2430">
                Estamos confirmando tu transacción con la pasarela.
              </p>
              <p className="text-gray-500 text-sm mb-8" data-id="element-2431">
                Esta página se actualizará automáticamente. No cierres la
                ventana.
              </p>
              <div className="bg-gray-50 rounded-xl p-4 text-left text-sm text-gray-600 max-w-sm mx-auto" data-id="element-2432">
                <p className="font-semibold text-brand-navy mb-1" data-id="element-2433">Referencia</p>
                <p className="font-mono text-xs break-all" data-id="element-2434">{reference}</p>
              </div>
            </>}

          {status === 'paid' && <>
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20
          }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-green/15 flex items-center justify-center" data-id="element-2435">
                <CheckCircle2 className="w-12 h-12 text-brand-green" data-id="element-2436" />
              </motion.div>
              <h1 className="text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-2437">
                ¡Pago confirmado! 🎉
              </h1>
              <p className="text-gray-600 mb-8 text-lg" data-id="element-2438">
                Tu {registrationType.toLowerCase()} fue registrada exitosamente.
              </p>

              {registration && <div className="bg-brand-cyan/5 border border-brand-cyan/20 rounded-2xl p-6 text-left mb-8" data-id="element-2439">
                  <h3 className="font-bold text-brand-navy mb-3" data-id="element-2440">
                    Detalles de tu inscripción
                  </h3>
                  <div className="space-y-2 text-sm" data-id="element-2441">
                    {registration.full_name && <div className="flex justify-between" data-id="element-2442">
                        <span className="text-gray-500" data-id="element-2443">Nombre</span>
                        <strong data-id="element-2444">{registration.full_name}</strong>
                      </div>}
                    {registration.team_name && <div className="flex justify-between" data-id="element-2445">
                        <span className="text-gray-500" data-id="element-2446">Equipo</span>
                        <strong data-id="element-2447">{registration.team_name}</strong>
                      </div>}
                    {registration.captain_name && <div className="flex justify-between" data-id="element-2448">
                        <span className="text-gray-500" data-id="element-2449">Capitán</span>
                        <strong data-id="element-2450">{registration.captain_name}</strong>
                      </div>}
                    {registration.responsible_name && <div className="flex justify-between" data-id="element-2451">
                        <span className="text-gray-500" data-id="element-2452">Responsable</span>
                        <strong data-id="element-2453">{registration.responsible_name}</strong>
                      </div>}
                    {registration.contact_name && <div className="flex justify-between" data-id="element-2454">
                        <span className="text-gray-500" data-id="element-2455">Contacto</span>
                        <strong data-id="element-2456">{registration.contact_name}</strong>
                      </div>}
                    {registration.company_name && <div className="flex justify-between" data-id="element-2457">
                        <span className="text-gray-500" data-id="element-2458">Empresa</span>
                        <strong data-id="element-2459">{registration.company_name}</strong>
                      </div>}
                    {registration.email && <div className="flex justify-between" data-id="element-2460">
                        <span className="text-gray-500" data-id="element-2461">Email</span>
                        <strong data-id="element-2462">{registration.email}</strong>
                      </div>}
                    {registration.captain_email && <div className="flex justify-between" data-id="element-2463">
                        <span className="text-gray-500" data-id="element-2464">Email capitán</span>
                        <strong data-id="element-2465">{registration.captain_email}</strong>
                      </div>}
                    {(registration.total_amount || registration.amount_cents) && <div className="flex justify-between pt-2 border-t border-brand-cyan/20" data-id="element-2466">
                        <span className="text-gray-500" data-id="element-2467">Total pagado</span>
                        <strong className="text-brand-navy" data-id="element-2468">
                          $
                          {((registration.total_amount || registration.amount_cents) / 100).toLocaleString('es-CO')}
                        </strong>
                      </div>}
                  </div>
                </div>}

              <p className="text-gray-500 text-sm mb-8" data-id="element-2469">
                Te enviamos un correo con todos los detalles. Revisa tu bandeja
                de entrada y spam.
              </p>

              <Link to="/" className="inline-flex items-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white px-8 py-3 rounded-full font-bold transition-all shadow-lg" data-id="element-2470">
                <Home className="w-4 h-4" data-id="element-2471" /> Volver al inicio
              </Link>
            </>}

          {status === 'failed' && <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2472">
                <XCircle className="w-12 h-12 text-red-500" data-id="element-2473" />
              </div>
              <h1 className="text-3xl font-bold text-brand-navy mb-3" data-id="element-2474">
                Tu pago no se pudo procesar
              </h1>
              <p className="text-gray-600 mb-8" data-id="element-2475">
                La transacción fue rechazada o expiró. Puedes intentarlo de
                nuevo.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white px-8 py-3 rounded-full font-bold transition-all shadow-lg" data-id="element-2476">
                <Home className="w-4 h-4" data-id="element-2477" /> Volver al inicio
              </Link>
            </>}

          {status === 'not_found' && <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-2478">
                <XCircle className="w-12 h-12 text-gray-400" data-id="element-2479" />
              </div>
              <h1 className="text-3xl font-bold text-brand-navy mb-3" data-id="element-2480">
                No encontramos esta inscripción
              </h1>
              <p className="text-gray-600 mb-8" data-id="element-2481">
                La referencia no existe o ya expiró. Si crees que es un error,
                contáctanos.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 bg-brand-cyan hover:bg-blue-700 text-white px-8 py-3 rounded-full font-bold transition-all shadow-lg" data-id="element-2482">
                <Home className="w-4 h-4" data-id="element-2483" /> Volver al inicio
              </Link>
            </>}
        </motion.div>
      </div>
    </div>;
}